import React from 'react';
import { Building2 } from 'lucide-react';

interface BusinessInfoSectionProps {
  companyName: string;
  companyEmail: string;
  companyWebsite: string;
  companyAddress: string;
  brandColor: string;
  themePrimary: string;
  fieldErrors?: Record<string, string>;
  onCompanyNameChange: (v: string) => void;
  onCompanyEmailChange: (v: string) => void;
  onCompanyWebsiteChange: (v: string) => void;
  onCompanyAddressChange: (v: string) => void;
  onBrandColorChange: (v: string) => void;
}

const PRESET_COLORS = [
  '#E22E51', '#E2652E', '#E2BF2E', '#99B431', '#1E7145',
  '#22577A', '#7E3877', '#E773BC', '#404040',
];

export function BusinessInfoSection({
  companyName,
  companyEmail,
  companyWebsite,
  companyAddress,
  brandColor,
  themePrimary,
  fieldErrors,
  onCompanyNameChange,
  onCompanyEmailChange,
  onCompanyWebsiteChange,
  onCompanyAddressChange,
  onBrandColorChange,
}: BusinessInfoSectionProps) {
  return (
    <div className="bg-card-background rounded-lg border border-border p-6">
      <div className="flex items-center mb-6">
        <Building2 className="h-5 w-5 text-text-secondary mr-2" />
        <h2 className="text-lg font-medium text-text-primary">Business Information</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="company-name" className="block text-sm font-medium text-text-primary mb-2">Company Name</label>
          <input
            id="company-name"
            type="text"
            value={companyName}
            onChange={(e) => onCompanyNameChange(e.target.value)}
            className={`input ${fieldErrors?.name ? 'border-red-500' : ''}`}
            style={{ '--tw-ring-color': themePrimary } as React.CSSProperties}
            placeholder="Enter your company name"
          />
          {fieldErrors?.name && <p className="mt-1 text-xs text-red-500">{fieldErrors.name}</p>}
        </div>

        <div>
          <label htmlFor="company-email" className="block text-sm font-medium text-text-primary mb-2">Business Email</label>
          <input
            id="company-email"
            type="email"
            value={companyEmail}
            onChange={(e) => onCompanyEmailChange(e.target.value)}
            className={`input ${fieldErrors?.email ? 'border-red-500' : ''}`}
            style={{ '--tw-ring-color': themePrimary } as React.CSSProperties}
            placeholder="contact@company.com"
          />
          {fieldErrors?.email && <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>}
        </div>

        <div>
          <label htmlFor="company-website" className="block text-sm font-medium text-text-primary mb-2">Website</label>
          <input
            id="company-website"
            type="url"
            value={companyWebsite}
            onChange={(e) => onCompanyWebsiteChange(e.target.value)}
            className={`input ${fieldErrors?.website ? 'border-red-500' : ''}`}
            style={{ '--tw-ring-color': themePrimary } as React.CSSProperties}
            placeholder="https://www.company.com"
          />
          {fieldErrors?.website && <p className="mt-1 text-xs text-red-500">{fieldErrors.website}</p>}
        </div>

        <div>
          <label htmlFor="company-address" className="block text-sm font-medium text-text-primary mb-2">Business Address</label>
          <input
            id="company-address"
            type="text"
            value={companyAddress}
            onChange={(e) => onCompanyAddressChange(e.target.value)}
            className={`input ${fieldErrors?.address ? 'border-red-500' : ''}`}
            style={{ '--tw-ring-color': themePrimary } as React.CSSProperties}
            placeholder="123 Main St, City, State 12345"
          />
          {fieldErrors?.address && <p className="mt-1 text-xs text-red-500">{fieldErrors.address}</p>}
        </div>

        {/* Brand Color Picker */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-text-primary mb-3">Brand Color</label>
          <div className="flex items-start gap-6">
            {/* Preview card */}
            <div className="shrink-0">
              <div className="bg-surface rounded-xl p-4 border border-border" style={{ width: '220px' }}>
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 min-w-8 rounded-lg flex items-center justify-center text-white font-bold shrink-0"
                    style={{ backgroundColor: brandColor, fontSize: '1rem' }}
                  >
                    {companyName.charAt(0).toUpperCase() || 'A'}
                  </div>
                  <div
                    className="text-text-primary font-semibold min-w-0 flex-1"
                    style={{
                      fontSize: '1.1875rem',
                      lineHeight: '1.25',
                      letterSpacing: '-0.02em',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical' as const,
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                    }}
                  >
                    {companyName || 'Company'}
                  </div>
                </div>
                <p className="text-xs text-text-secondary mt-3">Sidebar preview</p>
              </div>
            </div>

            {/* Color selection */}
            <div className="flex-1 space-y-3">
              <div>
                <p className="text-xs text-text-secondary mb-2">Choose a color</p>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => onBrandColorChange(color)}
                      className={`w-8 h-8 rounded-lg transition-all duration-150 ${
                        brandColor.toLowerCase() === color.toLowerCase()
                          ? 'scale-125'
                          : 'hover:scale-110'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <label className="relative cursor-pointer">
                  <input
                    type="color"
                    value={brandColor}
                    onChange={(e) => onBrandColorChange(e.target.value)}
                    className="sr-only"
                  />
                  <div
                    className="w-8 h-8 rounded-lg border-2 border-dashed border-border hover:border-text-secondary transition-colors flex items-center justify-center"
                    title="Pick custom color"
                  >
                    <span className="text-text-secondary text-lg">+</span>
                  </div>
                </label>
                <input
                  type="text"
                  value={brandColor}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                      onBrandColorChange(val);
                    }
                  }}
                  className="input w-24 font-mono text-sm py-1.5"
                  style={{ '--tw-ring-color': themePrimary } as React.CSSProperties}
                  placeholder="#22577A"
                />
                <span className="text-xs text-text-secondary">Custom</span>
              </div>
              {fieldErrors?.brandColor && <p className="mt-1 text-xs text-red-500">{fieldErrors.brandColor}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
